/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { GitLabConfig } from '../types';

export interface GitLabProject {
  id: number;
  name: string;
  path_with_namespace: string;
  description: string;
  visibility: string;
  web_url: string;
  http_url_to_repo: string;
  namespace: {
    name: string;
    path: string;
  };
}

export interface GitLabUser {
  id: number;
  username: string;
  name: string;
  email: string;
  avatar_url: string;
}

export class GitLabService {
  private config: GitLabConfig;
  private baseUrl: string;

  constructor(config: GitLabConfig) {
    this.config = config;
    // Default to gitlab.com if no instance URL provided
    this.baseUrl = config.instanceUrl 
      ? config.instanceUrl.replace(/\/$/, '') // Remove trailing slash
      : 'https://gitlab.com';
  }

  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const headers = {
      'Authorization': `Bearer ${this.config.accessToken}`,
      'Content-Type': 'application/json',
      ...options.headers,
    };

    const url = `${this.baseUrl}/api/v4${endpoint}`;
    
    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`GitLab API error: ${response.status} - ${errorText}`);
    }

    return response.json();
  }

  /**
   * Fetch all pages of a paginated GitLab API endpoint.
   * GitLab API has a max per_page of 100, so we need to paginate to get all results.
   */
  private async fetchAllPages<T>(endpoint: string): Promise<T[]> {
    const results: T[] = [];
    let page = 1;
    const perPage = 100;
    
    while (true) {
      const separator = endpoint.includes('?') ? '&' : '?';
      const url = `${this.baseUrl}/api/v4${endpoint}${separator}per_page=${perPage}&page=${page}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${this.config.accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`GitLab API error: ${response.status} - ${errorText}`);
      }

      const data = await response.json() as T[];
      results.push(...data);

      const totalPages = parseInt(response.headers.get('x-total-pages') || '1', 10);
      if (page >= totalPages) {
        break;
      }
      page++;
    }

    return results;
  }

  async testConnection(): Promise<{ success: boolean; error?: string; user?: GitLabUser }> {
    try {
      const user = await this.makeRequest<GitLabUser>('/user');
      return { success: true, user };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to connect to GitLab',
      };
    }
  }

  async getProjects(): Promise<GitLabProject[]> {
    try {
      // Get user's projects (membership=true means projects user is a member of)
      // Uses pagination to fetch all projects since GitLab API max per_page is 100
      const projects = await this.fetchAllPages<GitLabProject>(
        '/projects?membership=true&order_by=last_activity_at'
      );
      return projects;
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      return [];
    }
  }

  async getGroupProjects(groupId: string): Promise<GitLabProject[]> {
    try {
      // Uses pagination to fetch all projects since GitLab API max per_page is 100
      const projects = await this.fetchAllPages<GitLabProject>(
        `/groups/${groupId}/projects?order_by=last_activity_at`
      );
      return projects;
    } catch (error) {
      console.error('Failed to fetch group projects:', error);
      return [];
    }
  }

  async getProject(projectId: string): Promise<GitLabProject> {
    // projectId can be numeric ID or "namespace/project-name" format
    const encodedId = encodeURIComponent(projectId);
    return this.makeRequest<GitLabProject>(`/projects/${encodedId}`);
  }

  async validateProject(projectId: string): Promise<boolean> {
    try {
      await this.getProject(projectId);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Create a new GitLab project (repository)
   * This calls the backend endpoint which handles both GitLab API and local clone
   */
  async createRepository(options: {
    name: string;
    description?: string;
    isPrivate?: boolean;
  }): Promise<{
    success: boolean;
    repository?: {
      id: number;
      name: string;
      fullName: string;
      description: string;
      visibility: string;
      htmlUrl: string;
      cloneUrl: string;
      sshUrl: string;
    };
    localPath?: string;
    error?: string;
  }> {
    try {
      const apiUrl = import.meta.env.DEV 
        ? 'http://localhost:3002/api/gitlab/create-repo'
        : '/api/gitlab/create-repo';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: options.name,
          description: options.description || '',
          isPrivate: options.isPrivate ?? true,
          accessToken: this.config.accessToken,
          instanceUrl: this.baseUrl,
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        return {
          success: false,
          error: result.error || result.message || 'Failed to create project',
        };
      }

      return {
        success: true,
        repository: result.repository,
        localPath: result.localPath,
      };
    } catch (error: any) {
      console.error('Failed to create project:', error);
      return {
        success: false,
        error: error.message || 'Failed to create project',
      };
    }
  }
}

